import { Fragment, type ReactNode } from "react";
import { AppIcon, type AppIconKey } from "~/components/app-icon";
import type { SheetArt } from "~/lib/constants/sheet-art";

/**
 * The sheet vocabulary: the app-chrome slabs the inner pages use as proof.
 *
 * Each renders one shape from `SheetArt`, so the same artifact can run at hero
 * scale on one page and inside a card on another. Icons carry no alt text —
 * the label beside them already names the tool.
 */

interface BoardBarProps {
  icon?: AppIconKey;
  /** The Lindevo mark, when the slab is the system rather than a tool. */
  mark?: boolean;
  /** The "Example" marker on the right. */
  marker?: string;
  title: string;
}

export function BoardBar({
  icon,
  mark,
  marker,
  title,
}: Readonly<BoardBarProps>) {
  return (
    <div className="board-bar">
      {mark ? (
        <img alt="" height={22} src="/images/icon.svg" width={22} />
      ) : null}
      {icon ? <AppIcon name={icon} /> : null}
      <strong>{title}</strong>
      {marker ? <span>{marker}</span> : null}
    </div>
  );
}

export function SampleDocument({
  art,
}: Readonly<{ art: Extract<SheetArt, { kind: "sample-document" }> }>) {
  return (
    <div className="sample-document">
      <p className="icon-line">
        <AppIcon name={art.source.icon} />
        {art.source.label}
      </p>
      {art.heading ? <h3>{art.heading}</h3> : null}
      {art.body ? <p>{art.body}</p> : null}
      <p className="source-foot">
        {art.footIcon ? <AppIcon name={art.footIcon} /> : null}
        {art.foot}
      </p>
    </div>
  );
}

export function RoutingRows({
  art,
}: Readonly<{ art: Extract<SheetArt, { kind: "routing" }> }>) {
  const last = art.steps.length - 1;

  return (
    <div>
      {art.steps.map((step, index) => (
        <Fragment key={step.label}>
          <div className="routing-row">
            <AppIcon name={step.icon} />
            <div>
              <strong>{step.label}</strong>
              {step.detail ? <small>{step.detail}</small> : null}
            </div>
            {step.state ? <span>{step.state}</span> : null}
          </div>
          {index < last ? (
            <div aria-hidden="true" className="route-connector" />
          ) : null}
        </Fragment>
      ))}
    </div>
  );
}

export function AppSurface({
  art,
}: Readonly<{ art: Extract<SheetArt, { kind: "queue" }> }>) {
  return (
    <div className="app-surface">
      <BoardBar
        icon={art.bar.icon}
        mark={art.bar.mark}
        marker={art.bar.marker}
        title={art.bar.title}
      />
      {art.rows.map((row) => (
        <div
          className={row.flagged ? "queue-row is-flagged" : "queue-row"}
          key={row.label}
        >
          {row.label}
          <span>{row.state}</span>
        </div>
      ))}
    </div>
  );
}

export function DocumentFields({
  art,
}: Readonly<{ art: Extract<SheetArt, { kind: "document-fields" }> }>) {
  return (
    <div className="document-fields">
      {art.fields.map((field) => (
        <Fragment key={field.term}>
          <span>{field.term}</span>
          <strong>{field.value}</strong>
        </Fragment>
      ))}
    </div>
  );
}

/**
 * Weight 1–5 as a share of the row. The values stay under the 65% cap in
 * `sheets.css` and are spaced far enough apart to read as different lengths —
 * they are proportion, not data, which is why there is no axis and no scale.
 */
const BAR_SHARE: Record<1 | 2 | 3 | 4 | 5, string> = {
  1: "20%",
  2: "32%",
  3: "44%",
  4: "53%",
  5: "62%",
};

export function BarRows({
  art,
}: Readonly<{ art: Extract<SheetArt, { kind: "bar-rows" }> }>) {
  return (
    <div className="bar-rows">
      <p className="icon-line">
        {art.bar.icon ? <AppIcon name={art.bar.icon} /> : null}
        <strong>{art.bar.title}</strong>
      </p>
      {art.rows.map((row) => (
        <div key={row.label}>
          <span>{row.label}</span>
          <i style={{ width: BAR_SHARE[row.weight] }} />
        </div>
      ))}
      {art.note ? <p className="small-note">{art.note}</p> : null}
    </div>
  );
}

/** Renders whichever artifact a page's data declares. */
export function SheetArtwork({ art }: Readonly<{ art: SheetArt }>): ReactNode {
  switch (art.kind) {
    case "sample-document":
      return <SampleDocument art={art} />;
    case "routing":
      return <RoutingRows art={art} />;
    case "queue":
      return <AppSurface art={art} />;
    case "document-fields":
      return <DocumentFields art={art} />;
    default:
      return <BarRows art={art} />;
  }
}
